import { zodResolver } from "@hookform/resolvers/zod"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingIcon,
  Loader2,
} from "lucide-react"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import BrandLogo from "@/components/commons/app-brand-logo"
import { Button } from "@/components/ui/button"
import { FRONTEND_ROUTES } from "@/constants"
import { useWhitelabel } from "@/contexts/whitelabel"
import { useSSOAuth } from "@/hooks"
import type { ForgotPasswordFormSchemaProps } from "@/schemas/forgot-form"
import { forgotPasswordFormSchema } from "@/schemas/forgot-form"
import { StringHelper } from "@/utils"

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form"
import { Input } from "../ui/input"
export const SSOLoginForm = () => {
  const { t } = useTranslation()
  const { configuration } = useWhitelabel()
  const { mutateAsync: fetchSSOUrl, isPending } =
    useSSOAuth()
  const brand = import.meta.env.VITE_BRAND ?? "pay2b"
  const schema = useMemo(
    () => forgotPasswordFormSchema(t),
    [t]
  )
  const form = useForm<ForgotPasswordFormSchemaProps>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
    },
  })
  const handleSubmit = form.handleSubmit((values) => {
    const email = values.email.trim()
    fetchSSOUrl(StringHelper.getEmailDomain(email))
  })
  return (
    <div className="w-full max-w-[400px] space-y-8">
      {/* Logo e Título */}
      <div className="flex flex-col items-center space-y-6">
        <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl p-3">
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              backgroundColor: "var(--whitelabel-primary)",
              opacity: 0.1,
            }}
          />
          <BrandLogo
            brand={brand}
            className="relative z-10 h-10 w-10"
            customLogo={configuration?.merchant?.logo}
            customAlt={configuration?.merchant?.fantasyName}
          />
        </div>
        <div className="space-y-2 text-center">
          <h1 className="page-title tracking-tight text-foreground">
            Login Corporativo
          </h1>
          <p className="text-sm text-muted-foreground">
            Use seu e-mail corporativo para acessar via SSO
          </p>
        </div>
      </div>
      {/* Formulário */}
      <div className="rounded-2xl border border-border/50 bg-card p-8 shadow-lg shadow-black/5">
        <div className="mb-6 flex items-center justify-center gap-3 rounded-xl bg-muted/50 p-4">
          <BuildingIcon className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Autenticação via provedor de identidade
          </span>
        </div>
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-foreground">
                    E-mail corporativo
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="seu@empresa.com"
                      disabled={isPending}
                      className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex flex-col gap-3 pt-2">
              <Button
                type="submit"
                className="btn-whitelabel h-12 w-full rounded-xl text-base font-medium shadow-lg transition-all hover:shadow-xl"
                disabled={isPending}
              >
                {isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Continuar
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                type="button"
                className="h-12 w-full rounded-xl border-border/50 transition-all hover:bg-muted"
                asChild
              >
                <Link to={FRONTEND_ROUTES.LOGIN.href}>
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  Voltar ao login
                </Link>
              </Button>
            </div>
          </form>
        </Form>
      </div>
      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground">
        Seu provedor de identidade será identificado
        automaticamente pelo domínio do e-mail
      </p>
    </div>
  )
}
